/**
 * 1.8.4 §7 (D-1.8.4-7) — the box being typed in stays in view above the keyboard, in every sheet.
 *
 * When a text box, a text area or a choice list inside the sheet takes the focus, and once the keyboard has had time
 * to come up, the sheet's own scrolling part (`.sheet-b`) is scrolled — by setting its scrollTop, never with
 * scrollIntoView, which on an iPhone moves the visual viewport — just enough that the box is wholly inside what can
 * be seen: the part of the sheet above the keyboard. Once per focus; never from a scroll or viewport event (the 1.5.19
 * rule: a scroll or viewport event handler never scrolls anything). The page behind is not touched.
 */
import { useEffect, type RefObject } from 'react';

/** An iPhone's keyboard is up about this long after a box takes the focus (the Ask sheet's figure, 1.5.17). */
export const KEYBOARD_UP_MS = 350;
/** Room left above and below the box, so its label and the line under it show too. */
const MARGIN = 12;

const TYPES = /^(text|search|email|tel|url|number|password|date|time|datetime-local|month|week)$/;
export function takesTyping(el: Element | null): el is HTMLElement {
  if (!el) return false;
  if (el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement) return true;
  if (el instanceof HTMLInputElement) return TYPES.test(el.type || 'text');
  return (el as HTMLElement).isContentEditable === true;
}

/**
 * How far the scrolling part must move (positive: down) so a box between `top` and `bottom` is inside `[from, to]`
 * (all in screen pixels); 0 when it already is. A box taller than the room shows its top.
 */
export function nudgeFor(box: { top: number; bottom: number }, room: { from: number; to: number }, margin = MARGIN): number {
  const from = room.from + margin;
  const to = room.to - margin;
  if (box.top >= from && box.bottom <= to) return 0;
  if (box.bottom - box.top > to - from || box.top < from) return Math.round(box.top - from);
  return Math.round(box.bottom - to);
}

export function useKeepFieldInView(sheet: HTMLElement | null, body: RefObject<HTMLElement | null>): void {
  useEffect(() => {
    if (!sheet) return;
    let timer = 0;
    const onFocus = (e: FocusEvent) => {
      const field = e.target as Element | null;
      if (!takesTyping(field)) return;
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        const scroller = body.current;
        if (!scroller || document.activeElement !== field || !scroller.contains(field)) return;
        const r = field.getBoundingClientRect();
        const s = scroller.getBoundingClientRect();
        const vv = window.visualViewport;
        const seenTo = vv ? vv.offsetTop + vv.height : window.innerHeight;
        const seenFrom = vv ? vv.offsetTop : 0;
        const by = nudgeFor({ top: r.top, bottom: r.bottom }, { from: Math.max(s.top, seenFrom), to: Math.min(s.bottom, seenTo) });
        if (by) scroller.scrollTop += by;
      }, KEYBOARD_UP_MS);
    };
    sheet.addEventListener('focusin', onFocus);
    return () => {
      window.clearTimeout(timer);
      sheet.removeEventListener('focusin', onFocus);
    };
  }, [sheet, body]);
}
